// src/pages/DepartmentPage.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  fetchDepartments,
  createDepartment,
  updateDepartment,
  deleteDepartment,
  clearError,
  exportDepartments,
  importPreview,
  importConfirm,
} from 'store/slices/departmentSlice';
import { Pencil, Trash2, MoreHorizontal, Loader2, MoreVertical } from 'lucide-react';
import DataTable from '../../components/common/DataTable';
import PageHeader from '../../components/common/PageHeader';
import FormField from '../../components/common/FormField';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import { usePermissions } from 'hooks/usePermissions';
import usePermissionSocket from 'hooks/usePermissionSocket';
import { formatDateTime } from 'utlis/formateDateTime';
import { restoreRole } from 'store/slices/commonSlice';

const DepartmentPage = () => {
  usePermissionSocket(); // ← Auto-connects on mount

  const dispatch = useDispatch();
  const {
    departments,
    pagination,
    isLoading,
    error,
  } = useSelector((state) => state.department);

  const { canCreate, canUpdate, canDelete, canExport, canImport, canRead } = usePermissions('department');
  /* -------------------------- Pagination & Search -------------------------- */
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');

  /* -------------------------- Dialogs -------------------------- */
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [currentDepartment, setCurrentDepartment] = useState(null);
  const [formData, setFormData] = useState({ name: '', is_active: true });
  const [formErrors, setFormErrors] = useState({});

  /* -------------------------- REFETCH -------------------------- */
  const refetch = useCallback(() => {
    dispatch(fetchDepartments({ page, pageSize, search: searchTerm }));
  }, [dispatch, page, pageSize, searchTerm]);

  useEffect(() => {
    refetch();
  }, [refetch]);

  /* -------------------------- GLOBAL ERROR TOAST -------------------------- */
  useEffect(() => {
    if (error) {
      toast.error(typeof error === 'string' ? error : error.detail ?? 'An error occurred');
      dispatch(clearError());
    }
  }, [error, dispatch]);

  /* -------------------------- FORM VALIDATION -------------------------- */
  const validateForm = () => {
    const errors = {};
    if (!formData.name.trim()) errors.name = 'Department name is required';
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  /* -------------------------- DIALOG HANDLERS -------------------------- */
  const openDialog = (dept = null) => {
    if (dept) {
      if (!canUpdate) return toast.error("You don't have permission to edit");
      setCurrentDepartment(dept);
      setFormData({ name: dept.name, is_active: dept.is_active });
    } else {
      if (!canCreate) return toast.error("You don't have permission to create");
      setCurrentDepartment(null);
      setFormData({ name: '', is_active: true });
    }
    setFormErrors({});
    setIsDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    try {
      if (currentDepartment) {
        await dispatch(updateDepartment({ id: currentDepartment.id, ...formData })).unwrap();
        toast.success('Department updated');
      } else {
        await dispatch(createDepartment(formData)).unwrap();
        toast.success('Department created');
      }
      refetch();
      setIsDialogOpen(false);
    } catch {
      // error toasted globally
    }
  };

  const handleDelete = async () => {
    if (!canDelete) return toast.error("You don't have permission to delete");
    try {
      await dispatch(deleteDepartment(currentDepartment.id)).unwrap();
      toast.success('Department deleted');
      refetch();
      setIsDeleteDialogOpen(false);
    } catch { }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((p) => ({
      ...p,
      [name]: type === 'checkbox' ? checked : value,
    }));
    if (formErrors[name]) {
      setFormErrors((p) => ({ ...p, [name]: '' }));
    }
  };

  /* -------------------------- IMPORT CALLBACKS -------------------------- */
  const handleImportPreview = async (file) => {
    if (!canImport) throw new Error("You don't have import permission");
    const result = await dispatch(importPreview(file)).unwrap();
    return result;
  };

  const handleImportConfirm = async (preview) => {
    if (!canImport) throw new Error("You don't have import permission");
    await dispatch(importConfirm(preview)).unwrap();
    refetch();
  };

  /* -------------------------- TABLE COLUMNS -------------------------- */
  const columns = [
    {
      accessorKey: 'name',
      header: 'Department Name',
      cell: ({ row }) => <span className="font-medium">{row.original.name}</span>,
    },
    {
      accessorKey: 'is_active',
      header: 'Status',
      cell: ({ row }) => (
        <Badge variant={row.original.is_active ? 'success' : 'secondary'}>
          {row.original.is_active ? 'Active' : 'Inactive'}
        </Badge>
      ),
    },
    {
      accessorKey: 'created_at',
      header: 'Created On',
      cell: ({ row }) => <span className="text-sm">{formatDateTime(row.original.created_at) ?? '-'}</span>,
    },
    {
      accessorKey: 'created_by_name',
      header: 'Created By',
      cell: ({ row }) => <span className="text-sm">{row.original.created_by_name ?? '-'}</span>,
    },
    {
      id: 'actions',
      header: 'Actions',
      cell: ({ row }) => {
        const dept = row.original;
        return (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon">
                <MoreVertical className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {canUpdate && (
                <DropdownMenuItem onClick={() => openDialog(dept)}>
                  <Pencil className="mr-2 h-4 w-4" /> Edit
                </DropdownMenuItem>
              )}
              {canDelete && (
                <DropdownMenuItem
                  onClick={() => {
                    setCurrentDepartment(dept);
                    setIsDeleteDialogOpen(true);
                  }}
                  className="text-destructive"
                >
                  <Trash2 className="mr-2 h-4 w-4" /> Delete
                </DropdownMenuItem>
              )}
              {!canUpdate && !canDelete && (
                <DropdownMenuItem disabled>
                  No actions available
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        );
      },
    },
  ];

  /* -------------------------- RENDER -------------------------- */
  return (
    <div className="space-y-6 animate-fade-in">
      <PageHeader
        title="Departments"
        description="Manage your organization's departments"
        action={
          canCreate
            ? { label: 'Add Department', onClick: () => openDialog() }
            : null
        }
      />

      {/* {isLoading && !departments.length && (
        <div className="flex justify-center p-8">
          <Loader2 className="h-6 w-6 animate-spin mr-2" />
          <span>Loading departments...</span>
        </div>
      )} */}

      {canRead ?
        <DataTable
          data={departments}
          totalCount={pagination.count}
          columns={columns}
          templateUrl="/import-template/departments.csv"
          templateDownloadName="departments-import-template.csv"
          templateKey="department"
          searchPlaceholder="Search departments..."
          showExport={canExport}
          onExport={canExport ? () => dispatch(exportDepartments()) : undefined}
          showImport={canImport}
          onImportPreview={canImport ? handleImportPreview : undefined}
          onImportConfirm={canImport ? handleImportConfirm : undefined}
          pageIndex={page - 1}
          pageSize={pageSize}
          pageCount={Math.ceil(pagination.count / pagination.page_size)}
          onPageChange={(idx) => setPage(idx + 1)}
          onPageSizeChange={setPageSize}
          onSearch={setSearchTerm}
          isLoading={isLoading}
          onDelete={async (id) => {
            await dispatch(deleteDepartment(id));
            refetch();
          }}
          onRestore={async (id) => {
            await dispatch(restoreRole({ entityType: 'departments', id }));
            refetch();
          }}
        />
        : ""}

      {/* -------------------------- FORM DIALOG -------------------------- */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              {currentDepartment ? 'Edit Department' : 'Add Department'}
            </DialogTitle>
            <DialogDescription>
              {currentDepartment
                ? 'Update department details'
                : 'Create a new department'}
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4">
            <FormField
              label="Department Name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              error={formErrors.name}
              required
              placeholder="e.g., IT Department"
            />

            <FormField
              label="Active"
              name="is_active"
              type="switch"
              checked={formData.is_active}
              onCheckedChange={(c) => setFormData((p) => ({ ...p, is_active: c }))}
            />

            <div className="flex justify-end gap-2 pt-4">
              <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={isLoading}>
                {currentDepartment ? 'Update' : 'Create'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* -------------------------- DELETE CONFIRM -------------------------- */}
      <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Department?</AlertDialogTitle>
            <AlertDialogDescription>
              This will delete <strong>{currentDepartment?.name}</strong>.
              <br /><br />
              <strong>This action cannot be undone.</strong>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default DepartmentPage;